import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
} from "@mui/material";
import { useState, useMemo } from "react";
import { useCoolingRegimeTemperatureOptions, useCoolingRegimePriorityOptions } from "../../hooks/useCoolingRegimeProductGroup";

export interface ProductCoolingRegimeCreateDialogProps {
  open: boolean;
  onClose: () => void;
  productCode: number;
  initialValues?: {
    shortTermMain?: number | null;
    longTermMain?: number | null;
    shortTermDisplacementMain?: number | null;
    longTermDisplacementMain?: number | null;
    shortTermPriority?: number | null;
    longTermPriority?: number | null;
    relativeHumidity?: "J" | "N" | null;
  };
  onSubmit: (values: {
    productCode: number;
    coolingTemperatureCodeShortTerm?: number | null;
    coolingTemperatureCodeLongTerm?: number | null;
    coolingTemperatureCodeShortTermDisplacement?: number | null;
    coolingTemperatureCodeLongTermDisplacement?: number | null;
    coolingPriorityCodeShortTerm?: number | null;
    coolingPriorityCodeLongTerm?: number | null;
    relativeHumidityIndication?: string | null;
  }) => Promise<void> | void;
}

export function ProductCoolingRegimeCreateDialog({
  open,
  onClose,
  productCode,
  initialValues,
  onSubmit,
}: ProductCoolingRegimeCreateDialogProps) {
  const { data: temperatureOptions } = useCoolingRegimeTemperatureOptions();
  const { data: priorityOptions } = useCoolingRegimePriorityOptions();

  const [shortTermMain, setShortTermMain] = useState<number | "" | null>(initialValues?.shortTermMain ?? "");
  const [longTermMain, setLongTermMain] = useState<number | "" | null>(initialValues?.longTermMain ?? "");
  const [shortTermDisplacementMain, setShortTermDisplacementMain] = useState<number | "" | null>(initialValues?.shortTermDisplacementMain ?? "");
  const [longTermDisplacementMain, setLongTermDisplacementMain] = useState<number | "" | null>(initialValues?.longTermDisplacementMain ?? "");
  const [shortTermPriority, setShortTermPriority] = useState<number | "" | null>(initialValues?.shortTermPriority ?? "");
  const [longTermPriority, setLongTermPriority] = useState<number | "" | null>(initialValues?.longTermPriority ?? "");
  const [relativeHumidity, setRelativeHumidity] = useState<"J" | "N" | "" | null>(initialValues?.relativeHumidity ?? "");

  const canSubmit = useMemo(() => {
    // At least one of the fields should be provided to create
    return (
      shortTermMain !== "" ||
      longTermMain !== "" ||
      shortTermDisplacementMain !== "" ||
      longTermDisplacementMain !== "" ||
      shortTermPriority !== "" ||
      longTermPriority !== "" ||
      relativeHumidity !== ""
    );
  }, [shortTermMain, longTermMain, shortTermDisplacementMain, longTermDisplacementMain, shortTermPriority, longTermPriority, relativeHumidity]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onSubmit({
      productCode,
      coolingTemperatureCodeShortTerm: shortTermMain === "" ? null : (shortTermMain as number | null),
      coolingTemperatureCodeLongTerm: longTermMain === "" ? null : (longTermMain as number | null),
      coolingTemperatureCodeShortTermDisplacement: shortTermDisplacementMain === "" ? null : (shortTermDisplacementMain as number | null),
      coolingTemperatureCodeLongTermDisplacement: longTermDisplacementMain === "" ? null : (longTermDisplacementMain as number | null),
      coolingPriorityCodeShortTerm: shortTermPriority === "" ? null : (shortTermPriority as number | null),
      coolingPriorityCodeLongTerm: longTermPriority === "" ? null : (longTermPriority as number | null),
      relativeHumidityIndication: relativeHumidity === "" ? null : (relativeHumidity as string | null),
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Koelregime voor product aanmaken</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">Productcode: {productCode}</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Temperaturen</Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Regime &lt; 24u</InputLabel>
              <Select
                label="Regime < 24u"
                value={shortTermMain === null ? "" : shortTermMain}
                onChange={(e) => setShortTermMain(e.target.value === "" ? "" : Number(e.target.value))}
                displayEmpty
              >
                <MenuItem value=""><em>Geen</em></MenuItem>
                {temperatureOptions?.map(opt => (
                  <MenuItem key={opt.coolingRegimeTemperatureId ?? 0} value={opt.coolingRegimeTemperatureId ?? ""}>{opt.description}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Regime &gt; 24u</InputLabel>
              <Select
                label="Regime > 24u"
                value={longTermMain === null ? "" : longTermMain}
                onChange={(e) => setLongTermMain(e.target.value === "" ? "" : Number(e.target.value))}
                displayEmpty
              >
                <MenuItem value=""><em>Geen</em></MenuItem>
                {temperatureOptions?.map(opt => (
                  <MenuItem key={opt.coolingRegimeTemperatureId ?? 0} value={opt.coolingRegimeTemperatureId ?? ""}>{opt.description}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Uitwijk &lt; 24u</InputLabel>
              <Select
                label="Uitwijk < 24u"
                value={shortTermDisplacementMain === null ? "" : shortTermDisplacementMain}
                onChange={(e) => setShortTermDisplacementMain(e.target.value === "" ? "" : Number(e.target.value))}
                displayEmpty
              >
                <MenuItem value=""><em>Geen</em></MenuItem>
                {temperatureOptions?.map(opt => (
                  <MenuItem key={opt.coolingRegimeTemperatureId ?? 0} value={opt.coolingRegimeTemperatureId ?? ""}>{opt.description}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Uitwijk &gt; 24u</InputLabel>
              <Select
                label="Uitwijk > 24u"
                value={longTermDisplacementMain === null ? "" : longTermDisplacementMain}
                onChange={(e) => setLongTermDisplacementMain(e.target.value === "" ? "" : Number(e.target.value))}
                displayEmpty
              >
                <MenuItem value=""><em>Geen</em></MenuItem>
                {temperatureOptions?.map(opt => (
                  <MenuItem key={opt.coolingRegimeTemperatureId ?? 0} value={opt.coolingRegimeTemperatureId ?? ""}>{opt.description}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Prioriteiten en RV</Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Prioriteit &lt; 24u</InputLabel>
              <Select
                label="Prioriteit < 24u"
                value={shortTermPriority === null ? "" : shortTermPriority}
                onChange={(e) => setShortTermPriority(e.target.value === "" ? "" : Number(e.target.value))}
                displayEmpty
              >
                <MenuItem value=""><em>Geen</em></MenuItem>
                {priorityOptions?.map(opt => (
                  <MenuItem key={opt.coolingRegimePriorityId ?? 0} value={opt.coolingRegimePriorityId ?? ""}>{opt.description}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Prioriteit &gt; 24u</InputLabel>
              <Select
                label="Prioriteit > 24u"
                value={longTermPriority === null ? "" : longTermPriority}
                onChange={(e) => setLongTermPriority(e.target.value === "" ? "" : Number(e.target.value))}
                displayEmpty
              >
                <MenuItem value=""><em>Geen</em></MenuItem>
                {priorityOptions?.map(opt => (
                  <MenuItem key={opt.coolingRegimePriorityId ?? 0} value={opt.coolingRegimePriorityId ?? ""}>{opt.description}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Relatieve Vochtigheid</InputLabel>
              <Select
                label="Relatieve Vochtigheid"
                value={relativeHumidity ?? ""}
                onChange={(e) => setRelativeHumidity((e.target.value || "") as "J" | "N" | "")}
                displayEmpty
              >
                <MenuItem value=""><em>Geen</em></MenuItem>
                <MenuItem value="J">Ja</MenuItem>
                <MenuItem value="N">Nee</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">Annuleren</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>Aanmaken</Button>
      </DialogActions>
    </Dialog>
  );
}
